import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, SimpleNotificationContainer } from '../../components/ui';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

const loginSchema = z.object({
  username: z.string()
    .min(1, 'El nombre de usuario es requerido')
    .min(3, 'El nombre de usuario debe tener al menos 3 caracteres')
    .max(50, 'El nombre de usuario no puede exceder 50 caracteres'),
  password: z.string()
    .min(1, 'La contraseña es requerida')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuthStore();
  const { addNotification } = useUIStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setConnectionError(false);
    
    try {
      await login(data);
      addNotification({
        type: 'success',
        title: 'Inicio de sesión exitoso',
        message: 'Bienvenido al sistema de gestión de licencias',
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error type:', typeof error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Determinar el tipo de error y mostrar mensaje específico
      let errorTitle = 'Error de autenticación';
      let errorMessage = 'Ha ocurrido un error inesperado';
      let isConnectionError = false;
      
      if (error.message) {
        if (error.message.includes('Usuario no encontrado')) {
          errorTitle = 'Usuario no encontrado';
          errorMessage = 'El nombre de usuario no existe en el sistema. Verifica que esté escrito correctamente.';
        } else if (error.message.includes('Contraseña incorrecta')) {
          errorTitle = 'Contraseña incorrecta';
          errorMessage = 'La contraseña ingresada no es correcta. Verifica tu contraseña e intenta nuevamente.';
        } else if (error.message.includes('Usuario inactivo')) {
          errorTitle = 'Usuario inactivo';
          errorMessage = 'Tu cuenta ha sido desactivada. Contacta al administrador del sistema.';
        } else if (error.message.includes('Username y password son requeridos')) {
          errorTitle = 'Datos incompletos';
          errorMessage = 'Por favor, completa todos los campos requeridos.';
        } else if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
          errorTitle = 'Error de conexión';
          errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet e intenta nuevamente.';
          isConnectionError = true;
        } else if (error.message.includes('Error en el servidor')) {
          errorTitle = 'Error del servidor';
          errorMessage = 'El servidor está experimentando problemas. Intenta nuevamente en unos minutos.';
        } else if (error.message.includes('Unauthorized')) {
          errorTitle = 'Error de autenticación';
          errorMessage = 'Credenciales inválidas. Verifica tu usuario y contraseña.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setConnectionError(isConnectionError);
      
      addNotification({
        type: 'error',
        title: errorTitle,
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <KeyIcon className="h-8 w-8 text-primary-600" />
          </div>
          <div className="mt-6">
            <h1 className="text-4xl font-bold text-gray-900">
              DICOMITY
            </h1>
            <h2 className="text-2xl font-semibold text-gray-700 mt-1">
              License Manager
            </h2>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Inicia sesión en tu cuenta
          </p>
          
          {/* Connection Status Indicator */}
          {connectionError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">
                    <strong>Sin conexión:</strong> Verifica tu conexión a internet
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Login Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Iniciar Sesión</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                label="Nombre de Usuario"
                placeholder="Ingresa tu nombre de usuario"
                error={errors.username?.message}
                {...register('username')}
              />

              <Input
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                placeholder="Ingresa tu contraseña"
                error={errors.password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                {...register('password')}
              />

              <Button
                type="submit"
                className="w-full"
                loading={isSubmitting || isLoading}
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting || isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  ¿Olvidaste tu contraseña?
                </a>
              </div>
              
              {/* Retry Button for Connection Errors */}
              {connectionError && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => {
                    setConnectionError(false);
                    // Intentar reconectar verificando la conexión
                    fetch('/api/health/')
                      .then(() => setConnectionError(false))
                      .catch(() => setConnectionError(true));
                  }}
                >
                  Reintentar Conexión
                </Button>
              )}
            </form>

            {/* Demo Credentials */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Credenciales de Demo:
              </h3>
              <p className="text-xs text-gray-600">
                Usuario: <code className="bg-white px-1 rounded">admin</code>
              </p>
              <p className="text-xs text-gray-600">
                Contraseña: <code className="bg-white px-1 rounded">admin123</code>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 DICOMITY License Manager. Todos los derechos reservados.
          </p>
        </div>
      </div>
      
      {/* Global Notifications */}
      <SimpleNotificationContainer />
    </div>
  );
};

